import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import {
  absolutizeReplicatedImageSrcs,
  REPLICATED_IMAGE_PATH,
  type RpLayout,
} from '@mlm/shared';
import { RpPageRenderer, type RpPackageCard, type RpRenderCtx } from '@mlm/ui';
import { API_BASE_URL } from '../../lib/api';

/**
 * Live preview of the page being edited.
 *
 * It mounts `RpPageRenderer` — the SAME component the member app renders the
 * live page with. The pre-builder editor kept a second, hand-written preview
 * renderer, and the two had already drifted (preview drew an `<h2>` where live
 * drew an `<h1>`; a video previewed as a line of text). An admin who cannot
 * trust the preview never explores, which is most of why the old editor felt
 * limited. There is now no second renderer to drift.
 *
 * Known limitation of this in-page preview: CSS breakpoints are VIEWPORT
 * queries, so a narrow container still renders desktop styles — it cannot honestly
 * show a phone. That is what the iframe device preview is for; this pane is the
 * fidelity win, and the iframe is the responsive one.
 */
export function RpPreviewPane({
  layout,
  selectedId,
  onSelect,
  ctaLabel,
  ctaAuto,
  packages,
}: {
  layout: RpLayout;
  selectedId: string | null;
  onSelect: (id: string) => void;
  ctaLabel: string;
  ctaAuto: boolean;
  /** The page's tagged kits, so a `packages` section previews real cards. */
  packages: RpPackageCard[];
}) {
  const ctx = useMemo<RpRenderCtx>(
    () => ({
      // A sample ref so signup links resolve to something readable; the live
      // page substitutes the visitor's actual sponsor code.
      link: { ref: 'SAMPLE', campaignSlug: undefined },
      imageUrl: (key) => `${API_BASE_URL}${REPLICATED_IMAGE_PATH}?key=${encodeURIComponent(key)}`,
      legacyImageUrl: (blockId) => `${API_BASE_URL}/replicated-page/blocks/${blockId}/image`,
      // Sanitize here too. The server cleans stored HTML on save, but the
      // preview renders what is in the editor RIGHT NOW — authoring must never
      // be a trusted-HTML mode.
      renderHtml: (html) =>
        DOMPurify.sanitize(absolutizeReplicatedImageSrcs(html, API_BASE_URL), {
          ADD_TAGS: ['iframe'],
          ADD_ATTR: ['allowfullscreen', 'frameborder', 'target'],
        }),
      mode: 'preview',
      onSelectSection: onSelect,
      selectedSectionId: selectedId,
      packages,
    }),
    [onSelect, selectedId, packages],
  );

  return (
    <div className="overflow-hidden rounded-md border bg-background">
      {layout.sections.length === 0 ? (
        <p className="p-8 text-center text-sm text-muted-foreground">
          Nothing to preview yet. Add a section.
        </p>
      ) : (
        <RpPageRenderer
          layout={layout}
          ctx={ctx}
          className="min-h-0"
          footer={
            ctaAuto ? (
              <div className="pt-2 text-center">
                <span className="inline-block rounded-md bg-primary px-8 py-3 text-base font-semibold text-primary-foreground">
                  {ctaLabel}
                </span>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Added automatically. Its destination always carries the sponsor&apos;s referral code.
                </p>
              </div>
            ) : null
          }
        />
      )}
    </div>
  );
}
